import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { healthInsuranceCompanies } from "@/mocks/healthInsuranceCompanies";
import { useBrochureManager } from "@/hooks/useBrochureManager";
import SearchFilterBar from "./components/SearchFilterBar";
import CompanyCard from "./components/CompanyCard";
import ComparisonSection from "./components/ComparisonSection";
import ComparisonGuideModal from "./components/ComparisonGuideModal";

export default function HealthInsuranceCompanies() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInsurer, setSelectedInsurer] = useState("");

  const insurers = useMemo(
    () => healthInsuranceCompanies.map((c) => c.name),
    []
  );

  const totalPlans = useMemo(
    () => healthInsuranceCompanies.reduce((sum, c) => sum + c.plans.length, 0),
    []
  );

  const filteredCompanies = useMemo(() => {
    let companies = healthInsuranceCompanies;
    if (selectedInsurer) {
      companies = companies.filter((c) => c.name === selectedInsurer);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      companies = companies.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.plans.some(
            (p) =>
              p.name.toLowerCase().includes(q) ||
              p.bestSuitedFor.toLowerCase().includes(q)
          )
      );
    }
    return companies;
  }, [selectedInsurer, searchQuery]);

  const filteredCount = useMemo(
    () => filteredCompanies.reduce((sum, c) => sum + c.plans.length, 0),
    [filteredCompanies]
  );

  const baseUrl = siteUrl;
  const schemas = [
    webpageSchema(
      "Health Insurance Companies & Plan Brochures | ExpertAdvise.in",
      "Compare health insurance plans from HDFC ERGO, Niva Bupa, and ICICI Lombard. Download official brochures, compare features, and talk to an advisor.",
      `${baseUrl}/health-insurance-companies`
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      {
        name: "Health Insurance Companies",
        url: `${baseUrl}/health-insurance-companies`,
      },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Health Insurance Companies & Plan Brochures | ExpertAdvise.in"
        description="Compare health insurance plans from HDFC ERGO, Niva Bupa, and ICICI Lombard. Download official plan brochures, compare features side-by-side, and get expert advisor guidance."
        keywords="HDFC ERGO health insurance, Niva Bupa plans, ICICI Lombard health insurance, health insurance brochures, compare health insurance plans, Optima Secure, ReAssure, Elevate"
        canonical={`${baseUrl}/health-insurance-companies`}
        schemas={schemas}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative pt-28 md:pt-32 pb-12 md:pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900 via-primary-900 to-slate-900"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cta-600/15 via-transparent to-transparent"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-primary-600/10 via-transparent to-transparent"></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-6">
              <Link to="/" className="hover:text-white transition-colors">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-white/40"></i>
              <span className="text-white/90">Health Insurance Companies</span>
            </nav>

            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-cta-300 text-xs font-semibold border border-white/10 mb-4">
                <i className="ri-hospital-line"></i>
                Official Plan Brochures & Downloads
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                Health Insurance{" "}
                <span className="text-cta-300">Companies & Plan Brochures</span>
              </h1>
              <p className="text-base md:text-lg text-white/80 leading-relaxed mb-6 max-w-2xl">
                Explore associated health insurance providers, compare plans, and
                download official IRDAI-approved brochures directly from the
                insurers. Get expert guidance to choose the best coverage for you
                and your family.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/compare-plans"
                  className="inline-flex items-center gap-2 bg-cta-600 hover:bg-cta-700 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-arrow-left-right-line"></i>
                  Compare Plans
                </Link>
                <Link
                  to="/book-appointment?source=Health%20Insurers%20%26%20Brochures%20Page"
                  className="inline-flex items-center gap-2 bg-white hover:bg-white/90 text-slate-900 font-semibold text-sm px-5 py-2.5 rounded-full transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line"></i>
                  Talk to Advisor
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Search & Companies Section */}
        <section className="py-10 md:py-14 bg-slate-50/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-8">
            <SearchFilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedInsurer={selectedInsurer}
              onInsurerChange={setSelectedInsurer}
              insurers={insurers}
              totalPlans={totalPlans}
              filteredCount={filteredCount}
            />

            {/* Bulk Download Actions */}
            <ComparisonGuideModal />

            {/* Company Cards */}
            <div className="space-y-6">
              {filteredCompanies.length === 0 && (
                <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-search-line text-slate-400 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-1">
                    No plans found
                  </h3>
                  <p className="text-sm text-slate-500">
                    Try adjusting your search or filter criteria.
                  </p>
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedInsurer("");
                    }}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-50 text-primary-700 text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line"></i>
                    Clear Filters
                  </button>
                </div>
              )}

              {filteredCompanies.map((company) => (
                <CompanyCard
                  key={company.id}
                  company={company}
                  searchQuery={searchQuery}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <div className="bg-white">
          <ComparisonSection />
        </div>

        {/* Disclaimer Section */}
        <section className="py-10 md:py-14 bg-slate-50/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="bg-amber-50/80 border border-amber-200 rounded-2xl p-5 md:p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <i className="ri-information-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-amber-900 mb-1.5">
                    Important Disclaimer
                  </h3>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    Brochures and policy details are hosted on official insurer
                    websites. Please verify the latest terms, conditions,
                    exclusions, and IRDAI-approved documents before purchase.
                    Insurance is the subject matter of solicitation. ExpertAdvise.in
                    is an advisory platform and not an insurance provider or broker.
                    Premiums and coverage details may change without prior notice.
                    Always refer to the official policy wording document before
                    making a purchase decision.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-gradient-to-br from-primary-900 to-slate-900">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Not Sure Which Plan is Right for You?
            </h2>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto text-sm md:text-base">
              Our licensed insurance advisors will analyze your needs, compare plans
              across insurers, and recommend the best coverage for your family —
              completely free.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/book-appointment?source=Health%20Insurers%20%26%20Brochures%20Page"
                className="inline-flex items-center gap-2 bg-cta-600 hover:bg-cta-700 text-white font-bold text-sm md:text-base px-6 py-3 rounded-full transition-all shadow-lg shadow-cta-600/30 hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-calendar-check-line text-lg"></i>
                Book Free Consultation
              </Link>
              <a
                href="https://wa.me/919999365655?text=Hi%20ExpertAdvise%2C%20I%20need%20health%20insurance%20advice"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold text-sm md:text-base px-6 py-3 rounded-full transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                WhatsApp an Advisor
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingCTA />
    </div>
  );
}